import React, { useState } from 'react';
import { Button, Typography, Steps, Card, Checkbox, Space, Radio, Input, message, Alert } from 'antd';
import { 
  CarOutlined, 
  ToolOutlined,
  DashboardOutlined,
  CheckCircleOutlined 
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { 
  toggleDamageLocation, 
  setNoDamage,
  DamageLocation,
  isFrontDamage,
  isRearDamage,
  isSideDamage
} from '../../../redux/reducers/damageLocationsSlice';
import {
  togglePart,
  selectAllParts,
  clearCategoryParts,
  DamagePartCategory,
  getPartsForCategory
} from '../../../redux/reducers/damagedPartsSlice';
import { setVehicleDrivable, setAdditionalInformation } from '../../../redux/reducers/vehicleSlice';
import { VehicleDiagram } from '../../../components/VehicleDiagram';
import './DamageAssessmentStep.css';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

interface DamageAssessmentStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const DamageAssessmentStep: React.FC<DamageAssessmentStepProps> = ({ 
  onNext, 
  onPrevious 
}) => {
  const dispatch = useAppDispatch();
  const damageLocations = useAppSelector((state) => state.damageLocations);
  const damagedParts = useAppSelector((state) => state.damagedParts);
  const vehicle = useAppSelector((state) => state.vehicle);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [localNotes, setLocalNotes] = useState(vehicle.additionalInformation || '');

  const damageSteps = [
    {
      title: 'Damage Location',
      icon: <CarOutlined />,
    },
    {
      title: 'Damaged Parts',
      icon: <ToolOutlined />,
    },
    {
      title: 'Vehicle Status',
      icon: <DashboardOutlined />,
    }
  ];

  const handleLocationClick = (location: DamageLocation) => {
    dispatch(toggleDamageLocation(location));
  };

  const handleNoDamageToggle = (checked: boolean) => {
    dispatch(setNoDamage(checked));
  };

  const handlePartToggle = (category: DamagePartCategory, part: string) => {
    dispatch(togglePart({ category, part }));
  };

  const handleSelectAll = (category: DamagePartCategory) => {
    dispatch(selectAllParts(category));
  };

  const handleClearCategory = (category: DamagePartCategory) => {
    dispatch(clearCategoryParts(category));
  };

  const handleDrivableChange = (value: boolean) => {
    dispatch(setVehicleDrivable(value));
  };

  const handleNotesChange = (value: string) => {
    setLocalNotes(value);
    dispatch(setAdditionalInformation(value));
  };

  const canProceedFromLocation = () => {
    return damageLocations.noDamage || damageLocations.selectedLocations.length > 0;
  };


  const handleStepNext = () => {
    if (currentStep === 0 && !canProceedFromLocation()) {
      message.warning('Please select damage locations or indicate no damage');
      return;
    }
    
    if (currentStep < damageSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onNext();
    }
  };

  const handleStepPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onPrevious();
    }
  };

  const renderLocationStep = () => {
    const showFrontParts = isFrontDamage(damageLocations.selectedLocations);
    const showRearParts = isRearDamage(damageLocations.selectedLocations);
    const showSideParts = isSideDamage(damageLocations.selectedLocations);

    return (
      <div className="damage-location-step">
        <Card>
          <Title level={4}>Select Damage Locations</Title>
          <Paragraph type="secondary">
            Click on the vehicle diagram to mark all areas with damage
          </Paragraph>

          <VehicleDiagram
            selectedLocations={damageLocations.selectedLocations}
            onLocationClick={handleLocationClick}
            disabled={damageLocations.noDamage}
          />

          <div className="no-damage-option">
            <Checkbox
              checked={damageLocations.noDamage}
              onChange={(e) => handleNoDamageToggle(e.target.checked)}
            >
              <strong>No visible damage</strong>
            </Checkbox>
          </div>

          {damageLocations.selectedLocations.length > 0 && (
            <div className="damage-summary">
              <Text type="secondary">
                Parts selection will be available for: 
                {showFrontParts && ' Front'}
                {showRearParts && ' Rear'}
                {showSideParts && ' Side'}
              </Text>
            </div>
          )}
        </Card>
      </div>
    );
  };

  const renderPartsStep = () => {
    const showFrontParts = isFrontDamage(damageLocations.selectedLocations);
    const showRearParts = isRearDamage(damageLocations.selectedLocations);
    const showSideParts = isSideDamage(damageLocations.selectedLocations);

    const renderPartCategory = (category: DamagePartCategory, title: string) => {
      const parts = getPartsForCategory(damagedParts, category);
      const selectedParts = damagedParts.selectedParts[category];
      const allSelected = parts.length === selectedParts.length;

      return (
        <Card className="parts-category" key={category}>
          <div className="category-header">
            <Title level={5}>{title}</Title>
            <Space>
              <Button 
                size="small" 
                onClick={() => allSelected ? handleClearCategory(category) : handleSelectAll(category)}
              >
                {allSelected ? 'Clear All' : 'Select All'}
              </Button>
            </Space>
          </div>
          <div className="parts-grid">
            {parts.map((part) => (
              <Checkbox
                key={part}
                checked={selectedParts.includes(part)}
                onChange={() => handlePartToggle(category, part)}
              >
                {part}
              </Checkbox>
            ))}
          </div>
        </Card>
      );
    };

    return (
      <div className="damaged-parts-step">
        <Title level={4}>Select Damaged Parts</Title>
        <Paragraph type="secondary">
          Choose specific parts that are damaged (optional)
        </Paragraph>

        {!showFrontParts && !showRearParts && !showSideParts ? (
          <Alert
            message="No damage locations selected"
            description="Please go back and select damage locations to see relevant parts"
            type="info"
            showIcon
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {showFrontParts && renderPartCategory(DamagePartCategory.Front, 'Front Damage Parts')}
            {showRearParts && renderPartCategory(DamagePartCategory.Rear, 'Rear Damage Parts')}
            {showSideParts && renderPartCategory(DamagePartCategory.Side, 'Side Damage Parts')}
          </Space>
        )}
      </div>
    );
  };

  const renderStatusStep = () => {
    return (
      <div className="vehicle-status-step">
        <Card>
          <Title level={4}>Vehicle Status</Title>
          
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={5}>Is the vehicle drivable?</Title>
              <Radio.Group 
                value={vehicle.vehicleIsDrivable}
                onChange={(e) => handleDrivableChange(e.target.value)}
                size="large"
              >
                <Space direction="vertical">
                  <Radio value={true}>Yes, vehicle is drivable</Radio>
                  <Radio value={false}>No, vehicle is not drivable</Radio>
                </Space>
              </Radio.Group>
              
              {vehicle.vehicleIsDrivable === false && (
                <Alert
                  message="Non-Drivable Vehicle"
                  description="Additional documentation may be required for non-drivable vehicles. Please ensure all damage is thoroughly documented."
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              )}
            </div>

            <div>
              <Title level={5}>Additional Notes (Optional)</Title>
              <TextArea
                rows={4}
                value={localNotes}
                onChange={(e) => handleNotesChange(e.target.value)}
                placeholder="Enter any additional information about the damage or incident..."
                maxLength={1000}
                showCount
              />
            </div>
          </Space>
        </Card>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderLocationStep();
      case 1:
        return renderPartsStep();
      case 2:
        return renderStatusStep();
      default:
        return null;
    }
  };

  return (
    <div className="step-container damage-assessment-container">
      <Title level={3}>Damage Assessment</Title>
      <Text type="secondary">Document all vehicle damage</Text>

      <Steps 
        current={currentStep} 
        className="damage-sub-steps"
        size="small"
        style={{ marginTop: 24, marginBottom: 32 }}
      >
        {damageSteps.map((step, index) => (
          <Step key={index} title={step.title} icon={step.icon} />
        ))}
      </Steps>

      {renderCurrentStep()}

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button onClick={handleStepPrevious}>
          {currentStep === 0 ? 'Previous' : 'Back'}
        </Button>
        <Button 
          type="primary" 
          icon={currentStep === damageSteps.length - 1 ? <CheckCircleOutlined /> : undefined}
          onClick={handleStepNext}
        >
          {currentStep === damageSteps.length - 1 ? 'Continue' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default DamageAssessmentStep;